import ActionIcon from '@/components/_common/ActionIcon';
import {
  useWorkOrderItemActionList,
  WORKORDER_ITEM_ACTION_LIST,
} from '@/core/domain/workorder/WorkOrdersItems/WorkOrdersItems.hooks';
import { useDisclosure } from '@/hooks';
import { Button, Form, Modal } from 'antd';
import { Clock, List } from 'react-feather';
import { useWorkorderDetailContext } from '../../WorkorderDetailProvider';
import { FloatingFormItem, Group, InfiniteTable } from '@/components/_common';
import UsersInfiniteSelectField from '@/components/_common/UsersInfiniteSelectField';
import StatusTag from '@/components/_common/StatusTag';
import { WorkOrderItemActionTypeInfo } from '@/core/domain/workorder/WorkOrdersItems/WorkOrdersItems.types';
import moment from 'moment';
import WorkOrderItemActionExpandedItems from './ExpandedItems';
import { useState } from 'react';
import {
  workorderComplementColor,
  workorderComplementLabel,
} from '@/core/domain/workorder/WorkOrdersComplements/WorkOrdersComplements.types';
import { useQueryChanel } from '@/contexts/QueryChanelProvider';

export default function WorkOrderItemActionsModal() {
  const queryChanel = useQueryChanel();

  const [opened, action] = useDisclosure(false, {
    onClose: () => {
      form.resetFields();
      setExpandedKeys([]);
      queryChanel({ resetQueries: [WORKORDER_ITEM_ACTION_LIST] });
    },
  });

  const [expandedKeys, setExpandedKeys] = useState<number[]>([]);

  const [form] = Form.useForm<{ userIds: number[] }>();

  const userIds = Form.useWatch('userIds', form);

  const {
    complement: { data: complement },
  } = useWorkorderDetailContext();

  const itemActionList = useWorkOrderItemActionList(
    {
      complementId: complement?.id,
      userIds: userIds?.join(),
    },
    opened,
  );

  const history = itemActionList.data?.pages.flatMap((page) => page.data) || [];

  return (
    <>
      <ActionIcon onClick={action.open}>
        <Group gap={4} style={{ color: 'orange' }}>
          <Clock size={20} />
          <span style={{ fontWeight: 600 }}>Histórico</span>
        </Group>
      </ActionIcon>

      <Modal
        open={opened}
        onCancel={action.close}
        title="Histórico de alterações dos itens"
        width={1000}
        footer={false}
        maskClosable={false}
      >
        <p>
          Listagem de histórico de ações dos itens vinculados ao complemento por
          usuários
        </p>

        <Form form={form}>
          <FloatingFormItem
            name="userIds"
            label="Filtrar por usuário"
            initialValue={[]}
          >
            <UsersInfiniteSelectField mode="multiple" allowClear />
          </FloatingFormItem>
        </Form>

        <InfiniteTable
          key={expandedKeys[0] || 0}
          dataSource={history}
          loading={itemActionList.isLoading}
          fetchNextPage={itemActionList.fetchNextPage}
          hasNextPage={itemActionList.hasNextPage}
          rowKey={(item) => item.id}
          scroll={{ x: true }}
          expandable={{
            expandIcon: ({ onExpand, record }) => (
              <Button
                shape="circle"
                onClick={(e) => onExpand(record, e)}
                size="small"
                icon={<List size={14} />}
                type="primary"
                style={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                }}
              />
            ),
            expandedRowKeys: expandedKeys,
            onExpandedRowsChange: (keys) =>
              setExpandedKeys([keys.at(-1) as number]),
            rowExpandable: (item) => !!item.items?.length,
            expandedRowRender: (item) => (
              <WorkOrderItemActionExpandedItems item={item} />
            ),
          }}
          columns={[
            { title: 'Usuário', dataIndex: 'userName' },
            {
              title: 'Ação',
              render: (_, item) => (
                <StatusTag
                  color={WorkOrderItemActionTypeInfo[item.actionType].color}
                  label={WorkOrderItemActionTypeInfo[item.actionType].label}
                />
              ),
            },
            {
              title: 'Itens',
              render: (_, item) => {
                const itemsCount = item.items?.length || 0;
                return `${
                  itemsCount > 1 ? `${itemsCount} items` : `${itemsCount} item`
                }`;
              },
            },
            {
              title: 'Status (complemento)',
              render: (_, item) => (
                <StatusTag
                  label={
                    item.complementStatus
                      ? workorderComplementLabel[item.complementStatus]
                      : 'Não definido'
                  }
                  color={
                    item.complementStatus
                      ? workorderComplementColor[item.complementStatus]
                      : 'default'
                  }
                />
              ),
            },
            {
              title: 'Data/Hora',
              align: 'right',
              render: (_, item) =>
                item.actionAt
                  ? moment(item.actionAt).format('DD/MM/YYYY HH:mm')
                  : '-',
            },
          ]}
        />
      </Modal>
    </>
  );
}
